import * as React from "react";
import { KeyRound } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import {
  findNextIncompleteTraining,
  ListViewUserCertificationCard,
} from "../../../ui/components/ListView/components/ListViewUserCertificationCard";
import type {
  CertificationTrainingMilestone,
  ListViewUserCertificationCardModule,
  ListViewUserCertificationCardStatus,
} from "../../../ui/components/ListView/components/ListViewUserCertificationCard";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import {
  fetchCertificationModule,
  fetchCertificationModuleAdmin,
  fetchCertificationModules,
  fetchMyCertifications,
  fetchUserCertifications,
  type CertificationModuleDetail,
  type CertificationTraining,
} from "../../lib/api/certifications";
import {
  createOrgCertificationAccessRequest,
  fetchOrgUserCertifications,
  type OrgCertMember,
} from "../../lib/api/certifications-org";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import { useI18n } from "../../lib/i18n";
import { SendCertificationReminderModal } from "../organizations/SendCertificationReminderModal";
import { JsonInspectorModal } from "../common/JsonInspectorModal";
import { downloadCertificatePdf } from "../../lib/certificate/certificatePdf";
import * as styles from "./UserCertificationsSection.css";

type CertificationRow = ListViewUserCertificationCardModule & { id: string };

type ProgressSource = {
  moduleId: string;
  moduleTitle?: string;
  title?: string;
  progressPercent: number;
  completedAt: string | null;
  status: string;
  expiresAt: string | null;
  trophyImageUrl?: string | null;
  trainings?: CertificationTrainingMilestone[];
  certificateNumber?: string | null;
};

const STAT_SLOTS = [
  { id: "installations", label: "Sanctuary 2 installations completed", value: "—" },
  { id: "last-install", label: "Last installation", value: "—" },
] as const;

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) {
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

function normalizeStatus(status: string): ListViewUserCertificationCardStatus {
  switch (status) {
    case "complete":
    case "in_progress":
    case "retake_needed":
    case "not_started":
      return status;
    default:
      return "not_started";
  }
}

function toIso(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === "string") return value;
  try {
    return value.toISOString();
  } catch {
    return null;
  }
}

function sortTrainings(trainings: CertificationTraining[]): CertificationTraining[] {
  return [...trainings].sort(
    (a, b) =>
      a.stepNumber - b.stepNumber ||
      a.sortOrder - b.sortOrder ||
      a.title.localeCompare(b.title),
  );
}

function trainingToMilestone(
  t: CertificationTraining,
  completed: boolean,
  index: number,
): CertificationTrainingMilestone {
  return {
    id: t.id,
    title: t.title,
    section: t.section,
    completed,
    stepNumber: t.stepNumber || index + 1,
    videoUrl: t.videoUrl,
    thumbnailUrl: t.thumbnailUrl ?? null,
    questionCount: t.questionCount ?? t.questions?.length ?? 0,
    videoDurationSeconds: t.videoDurationSeconds ?? null,
    userProgress: t.userProgress ?? null,
    videoWatchedPercent: t.videoWatchedPercent ?? 0,
  };
}

function milestonesFromDetail(
  detail: CertificationModuleDetail | null | undefined,
  progressPercent: number,
  status: ListViewUserCertificationCardStatus,
): CertificationTrainingMilestone[] {
  const trainings = sortTrainings(detail?.trainings ?? []);
  if (trainings.length === 0) return [];

  const hasPerTraining = trainings.some((t) => t.userProgress != null);
  if (hasPerTraining) {
    return trainings.map((t, i) =>
      trainingToMilestone(t, Boolean(t.userProgress?.passed), i),
    );
  }

  if (status === "complete") {
    return trainings.map((t, i) => trainingToMilestone(t, true, i));
  }

  // Fallback when viewing another user without per-training progress:
  // mark the first N ordered trainings complete to match overall percent.
  const passedCount = Math.round(
    (Math.max(0, Math.min(100, progressPercent)) / 100) * trainings.length,
  );
  return trainings.map((t, i) =>
    trainingToMilestone(t, i < passedCount, i),
  );
}

type ModuleCatalogEntry = {
  id: string;
  title: string;
  heroImageUrl?: string | null;
  trophyImageUrl?: string | null;
  certificateHeaderText?: string | null;
  certificateCourseText?: string | null;
  sortOrder?: number;
};

/** Raw sources behind one card's transformed data, for the super-admin "View data" debug modal. */
export type CertificationDebugData = {
  catalog: ModuleCatalogEntry;
  progress: ProgressSource | null;
  detail: CertificationModuleDetail | null;
  transformed: ListViewUserCertificationCardModule;
};

function joinModulesWithProgress(
  modules: ModuleCatalogEntry[],
  progress: ProgressSource[],
  detailsById: Map<string, CertificationModuleDetail | null>,
): CertificationDebugData[] {
  const byModuleId = new Map(progress.map((p) => [p.moduleId, p]));
  return [...modules]
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.title.localeCompare(b.title),
    )
    .map((m) => {
      const p = byModuleId.get(m.id);
      const status = p ? normalizeStatus(p.status) : "not_started";
      const progressPercent = p?.progressPercent ?? 0;
      const detail = detailsById.get(m.id) ?? null;
      const trainings =
        p?.trainings ??
        milestonesFromDetail(detail, progressPercent, status);
      const transformed: ListViewUserCertificationCardModule = {
        moduleId: m.id,
        moduleTitle: m.title,
        heroImageUrl: m.heroImageUrl ?? detail?.heroImageUrl ?? null,
        trophyImageUrl:
          m.trophyImageUrl ?? p?.trophyImageUrl ?? detail?.trophyImageUrl ?? null,
        progressPercent,
        completedAt: toIso(p?.completedAt),
        status,
        expiresAt: toIso(p?.expiresAt),
        certificateNumber: p?.certificateNumber ?? null,
        trainings,
      };
      return { catalog: m, progress: p ?? null, detail, transformed };
    });
}

async function loadModuleDetails(
  token: string,
  moduleIds: string[],
  selfView: boolean,
): Promise<Map<string, CertificationModuleDetail | null>> {
  const map = new Map<string, CertificationModuleDetail | null>();
  await Promise.all(
    moduleIds.map(async (id) => {
      try {
        if (selfView) {
          map.set(id, await fetchCertificationModule(token, id));
          return;
        }
        try {
          map.set(id, await fetchCertificationModuleAdmin(token, id));
        } catch {
          // Student detail includes the viewer's progress — strip it so we
          // don't attribute another user's training completion incorrectly.
          const detail = await fetchCertificationModule(token, id);
          map.set(id, {
            ...detail,
            enrollment: null,
            trainings: (detail.trainings ?? []).map((t) => ({
              ...t,
              userProgress: null,
              videoWatchedPercent: 0,
              watchedSeconds: 0,
            })),
          });
        }
      } catch {
        map.set(id, null);
      }
    }),
  );
  return map;
}

async function loadProgressRows(
  token: string,
  userId: string,
  selfView: boolean,
  organizationId: string | undefined,
): Promise<{ progress: ProgressSource[]; orgMember: OrgCertMember | null }> {
  if (selfView) {
    const mine = await fetchMyCertifications(token);
    return {
      progress: mine.map((c) => ({
        moduleId: c.moduleId,
        progressPercent: c.progressPercent,
        completedAt: c.completedAt,
        status: c.status,
        expiresAt: c.expiresAt,
        certificateNumber: c.certificateNumber,
      })),
      orgMember: null,
    };
  }

  if (organizationId) {
    try {
      const org = await fetchOrgUserCertifications(token, organizationId, userId);
      return {
        progress: (org.modules ?? []).map((m) => ({
          moduleId: m.moduleId,
          title: m.title,
          trophyImageUrl: m.trophyImageUrl,
          progressPercent: m.progressPercent,
          completedAt: m.completedAt,
          status: m.status,
          expiresAt: m.expiresAt,
          certificateNumber: m.certificateNumber,
        })),
        orgMember: org,
      };
    } catch {
      // Fall through to admin endpoint when org-scoped fetch is unavailable.
    }
  }

  try {
    const admin = await fetchUserCertifications(token, userId);
    return {
      progress: admin.map((m) => ({
        moduleId: m.moduleId,
        moduleTitle: m.moduleTitle,
        trophyImageUrl: m.trophyImageUrl,
        progressPercent: m.progressPercent,
        completedAt: m.completedAt,
        status: m.status,
        expiresAt: m.expiresAt,
        certificateNumber: m.certificateNumber,
      })),
      orgMember: null,
    };
  } catch (e) {
    if (e instanceof ApiClientError || e instanceof ProApiClientError) {
      if (e.status === 404) {
        // No certification records for this user yet — not an error.
        return { progress: [], orgMember: null };
      }
    }
    throw e;
  }
}

export function UserCertificationsSection({
  userId,
  token,
  userLabel = "this user",
  selfView = false,
  canManage = false,
  canEnroll = true,
  isSuperAdmin = false,
  organizationId,
  reminderTarget,
  onRequestAccess,
  onHasDataChange,
}: {
  userId: string;
  token: string;
  userLabel?: string;
  /** True when the viewer is looking at their own profile — fetches via `/certifications/me`. */
  selfView?: boolean;
  /** Org `certification:manage` (or equivalent) for the current org context. */
  canManage?: boolean;
  /** Shows the "View data" debug button (raw + transformed JSON per module). */
  isSuperAdmin?: boolean;
  /** Org `certification:view` (or self / super-admin). Used by parents for section gating. */
  canView?: boolean;
  /** Org `certification:enroll`. When false on self + org context, shows access-request CTA. */
  canEnroll?: boolean;
  /** Org context for scoped fetch + access request. */
  organizationId?: string;
  /**
   * Target user for manager "Send reminder". When omitted, built from the
   * org-scoped certifications response when available.
   */
  reminderTarget?: Pick<
    OrgCertMember,
    | "userId"
    | "firstName"
    | "lastName"
    | "name"
    | "email"
    | "avatarUrl"
  >;
  /**
   * Optional override for the access-request CTA. When omitted, the section
   * POSTs `/organizations/:id/certification-access-requests`.
   */
  onRequestAccess?: () => void | Promise<void>;
  /** Notified once loading settles so parents can gate nav / section visibility. */
  onHasDataChange?: (hasData: boolean) => void;
}) {
  const { t, currentLanguage } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const { subscribe, unsubscribe } = useWebSocket();
  const [modules, setModules] = React.useState<ListViewUserCertificationCardModule[]>([]);
  const modulesRef = React.useRef(modules);
  modulesRef.current = modules;
  const debugDataRef = React.useRef<Map<string, CertificationDebugData>>(new Map());
  const [debugModuleId, setDebugModuleId] = React.useState<string | null>(null);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  const [forcedFlashIds, setForcedFlashIds] = React.useState(
    () => new Map<string, number>(),
  );
  const pendingFlashIdsRef = React.useRef<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [accessRequesting, setAccessRequesting] = React.useState(false);
  const [accessRequestDone, setAccessRequestDone] = React.useState(false);
  const [accessRequestError, setAccessRequestError] = React.useState<string | null>(null);
  const [orgMember, setOrgMember] = React.useState<OrgCertMember | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = React.useState(false);
  /** userId → `moduleId:trainingId` default for the reminder picker. */
  const [reminderInitialOverrides, setReminderInitialOverrides] = React.useState<
    Record<string, string>
  >({});

  const bumpForcedFlashes = React.useCallback((ids: string[]) => {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return;
    setForcedFlashIds((prev) => {
      const next = new Map(prev);
      for (const id of unique) next.set(id, (next.get(id) ?? 0) + 1);
      return next;
    });
  }, []);

  const showAccessRequestCta =
    selfView && Boolean(organizationId) && !canEnroll;

  const resolvedReminderMember = React.useMemo((): OrgCertMember | null => {
    if (orgMember) return orgMember;
    if (!reminderTarget) return null;
    return {
      userId: reminderTarget.userId,
      firstName: reminderTarget.firstName,
      lastName: reminderTarget.lastName,
      name: reminderTarget.name,
      email: reminderTarget.email,
      avatarUrl: reminderTarget.avatarUrl,
      role: null,
      modules: [],
    };
  }, [orgMember, reminderTarget]);

  const canSendReminder =
    !selfView &&
    canManage &&
    Boolean(organizationId) &&
    resolvedReminderMember != null;

  const openReminderForModule = React.useCallback(
    (moduleId: string) => {
      if (!resolvedReminderMember) return;
      const row = modules.find((m) => m.moduleId === moduleId);
      const next = findNextIncompleteTraining(row?.trainings);
      setReminderInitialOverrides(
        next
          ? { [resolvedReminderMember.userId]: `${moduleId}:${next.id}` }
          : {},
      );
      setReminderModalOpen(true);
    },
    [modules, resolvedReminderMember],
  );

  const recipientName = React.useMemo(() => {
    if (!reminderTarget) return userLabel;
    const full = [reminderTarget.firstName, reminderTarget.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return full || reminderTarget.name || reminderTarget.email;
  }, [reminderTarget, userLabel]);

  const [downloadingModuleId, setDownloadingModuleId] = React.useState<
    string | null
  >(null);

  const downloadCertificate = React.useCallback(
    async (moduleId: string) => {
      const row = modules.find((m) => m.moduleId === moduleId);
      if (!row) return;
      const catalog = debugDataRef.current.get(moduleId)?.catalog;
      const resolvedCourseTitle = resolve(row.moduleTitle).trim() || row.moduleTitle;
      const headerTextOverride = catalog?.certificateHeaderText
        ? resolve(catalog.certificateHeaderText).trim()
        : "";
      const courseTextOverride = catalog?.certificateCourseText
        ? resolve(catalog.certificateCourseText).trim()
        : "";
      setDownloadingModuleId(moduleId);
      try {
        downloadCertificatePdf({
          recipientName,
          courseTitle: courseTextOverride || resolvedCourseTitle,
          completedAt: row.completedAt,
          certificateNumber: row.certificateNumber ?? null,
          labels: {
            heading: headerTextOverride || t("certification.certificate.heading"),
            presentedTo: t("certification.certificate.presentedTo"),
            completionLine: t("certification.certificate.completionLine"),
            completedOn: t("certification.quiz.completedOn"),
            certificateNumber: t("certification.certificate.numberLabel"),
            issuedBy: t("certification.certificate.issuedBy"),
          },
          locale: currentLanguage?.code,
        });
      } finally {
        setDownloadingModuleId(null);
      }
    },
    [modules, recipientName, resolve, t, currentLanguage?.code],
  );

  const openDebugForModule = React.useCallback((moduleId: string) => {
    setDebugModuleId(moduleId);
  }, []);

  const loadCertifications = React.useCallback(
    async (opts?: { soft?: boolean }) => {
      const soft = Boolean(opts?.soft);
      if (!soft) {
        setLoading(true);
        setError(null);
        setOrgMember(null);
      }
      try {
        const [catalog, progressResult] = await Promise.all([
          fetchCertificationModules(token),
          loadProgressRows(token, userId, selfView, organizationId),
        ]);
        if (progressResult.orgMember) {
          setOrgMember(progressResult.orgMember);
        }

        const detailsById = await loadModuleDetails(
          token,
          catalog.map((m) => m.id),
          selfView,
        );
        const joined = joinModulesWithProgress(
          catalog,
          progressResult.progress,
          detailsById,
        );
        const transformed = joined.map((j) => j.transformed);
        debugDataRef.current = new Map(joined.map((j) => [j.transformed.moduleId, j]));

        if (soft) {
          const changedIds: string[] = [];
          const prevById = new Map(
            modulesRef.current.map((m) => [m.moduleId, m]),
          );
          for (const row of transformed) {
            const prev = prevById.get(row.moduleId);
            if (
              !prev ||
              prev.progressPercent !== row.progressPercent ||
              prev.status !== row.status ||
              prev.completedAt !== row.completedAt ||
              prev.expiresAt !== row.expiresAt ||
              JSON.stringify(prev.trainings) !== JSON.stringify(row.trainings)
            ) {
              changedIds.push(row.moduleId);
            }
          }
          for (const id of pendingFlashIdsRef.current) changedIds.push(id);
          pendingFlashIdsRef.current.clear();
          setModules(transformed);
          setListRefreshKey((k) => k + 1);
          bumpForcedFlashes(changedIds);
        } else {
          pendingFlashIdsRef.current.clear();
          setModules(transformed);
          setListRefreshKey((k) => k + 1);
        }

        setError(null);
        onHasDataChange?.(transformed.length > 0 || showAccessRequestCta || selfView);
      } catch (e) {
        if (!soft) {
          setError(getErrorMessage(e, "Failed to load certifications"));
          setModules([]);
          onHasDataChange?.(showAccessRequestCta || selfView);
        }
      } finally {
        if (!soft) setLoading(false);
      }
    },
    [
      token,
      userId,
      selfView,
      organizationId,
      onHasDataChange,
      showAccessRequestCta,
      bumpForcedFlashes,
    ],
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      await loadCertifications();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCertifications]);

  React.useEffect(() => {
    if (!userId) return;
    const topic = `entity:certifications:user:${userId}`;
    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as { moduleId?: string } | undefined;
      if (typeof payload?.moduleId === "string" && payload.moduleId) {
        pendingFlashIdsRef.current.add(payload.moduleId);
      }
      void loadCertifications({ soft: true });
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [userId, subscribe, unsubscribe, loadCertifications]);

  const columns = React.useMemo<ColumnDefinition<CertificationRow>[]>(
    () => [
      {
        id: "title",
        label: "Certification",
        render: (row) => (
          <span style={{ fontWeight: 600 }}>
            {resolve(row.moduleTitle).trim() || row.moduleTitle}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        render: (row) => <span>{row.status}</span>,
      },
    ],
    [resolve],
  );

  const rows = React.useMemo<CertificationRow[]>(
    () => modules.map((m) => ({ ...m, id: m.moduleId })),
    [modules],
  );

  const fetchList = React.useCallback(
    async (params: ListParams): Promise<ListResult<CertificationRow>> => {
      const q = (params.search ?? "").trim().toLowerCase();
      let filtered = rows;
      if (q) {
        filtered = rows.filter((r) => {
          const title = (resolve(r.moduleTitle).trim() || r.moduleTitle).toLowerCase();
          return title.includes(q) || r.status.toLowerCase().includes(q);
        });
      }
      const page = params.page ?? 1;
      const pageSize = params.limit ?? 50;
      const start = (page - 1) * pageSize;
      return {
        data: filtered.slice(start, start + pageSize),
        total: filtered.length,
      };
    },
    [rows, resolve],
  );

  const handleRequestAccess = React.useCallback(async () => {
    if (!organizationId) return;
    setAccessRequestError(null);
    setAccessRequesting(true);
    try {
      if (onRequestAccess) {
        await onRequestAccess();
      } else {
        await createOrgCertificationAccessRequest(token, organizationId);
      }
      setAccessRequestDone(true);
    } catch (e) {
      setAccessRequestError(
        getErrorMessage(e, "Failed to request certification access"),
      );
    } finally {
      setAccessRequesting(false);
    }
  }, [organizationId, onRequestAccess, token]);

  if (loading) {
    return (
      <Text level="body-medium" style={{ color: "var(--color-fgSecondary)" }}>
        Loading certifications…
      </Text>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.statsStrip} aria-label={t("pro.certifications.stats")}>
        {STAT_SLOTS.map((slot) => (
          <div key={slot.id} className={styles.statSlot}>
            <Text level="body-small" className={styles.statLabel}>
              {slot.label}
            </Text>
            <Text level="body-medium" weight="bold" className={styles.statValue}>
              {slot.value}
            </Text>
          </div>
        ))}
      </div>

      {showAccessRequestCta ? (
        <div className={styles.accessRequest}>
          {accessRequestError ? (
            <Callout variant="error">{accessRequestError}</Callout>
          ) : null}
          {accessRequestDone ? (
            <Callout variant="success">
              Certification access requested. An organization manager will review
              your request.
            </Callout>
          ) : (
            <div className={styles.accessRequestRow}>
              <Text
                level="body-medium"
                style={{ color: "var(--color-fgSecondary)", flex: 1 }}
              >
                You don&apos;t have permission to enroll in certifications for
                this organization.
              </Text>
              <Button
                type="button"
                color="primary"
                variant="solid"
                iconLeft={<KeyRound size={16} />}
                loading={accessRequesting}
                onClick={() => void handleRequestAccess()}
              >
                Request certification access
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {error && modules.length === 0 ? (
        <Text level="body-medium" style={{ color: "var(--color-fgSecondary)" }}>
          {error}
        </Text>
      ) : (
        <ListView<CertificationRow>
          key={`user-certs-${userId}`}
          listId={`user-certifications-${userId}`}
          columns={columns}
          fetchList={fetchList}
          enabledViews={["stack", "deck"]}
          contextKey={`${listRefreshKey}-${rows.length}`}
          forcedFlashIds={forcedFlashIds}
          queryOptions={{ staleTime: Infinity }}
          disableLocalPersistence
          deckCardHeight={340}
          searchPlaceholder="Search certifications…"
          emptyTitle={`${userLabel} has no certifications yet.`}
          emptyIcon="users"
          getRowLabel={(row) =>
            resolve(row.moduleTitle).trim() || row.moduleTitle
          }
          renderCard={(row) => (
            <ListViewUserCertificationCard
              layout="deck"
              certification={row}
              selfView={selfView}
              canManage={canSendReminder}
              onSendReminder={
                canSendReminder ? openReminderForModule : undefined
              }
              onDownloadCertificate={() => void downloadCertificate(row.moduleId)}
              generatingCertificate={downloadingModuleId === row.moduleId}
              isSuperAdmin={isSuperAdmin}
              onViewData={isSuperAdmin ? openDebugForModule : undefined}
            />
          )}
          renderStackCard={(row) => (
            <ListViewUserCertificationCard
              layout="stack"
              certification={row}
              selfView={selfView}
              canManage={canSendReminder}
              onSendReminder={
                canSendReminder ? openReminderForModule : undefined
              }
              onDownloadCertificate={() => void downloadCertificate(row.moduleId)}
              generatingCertificate={downloadingModuleId === row.moduleId}
              isSuperAdmin={isSuperAdmin}
              onViewData={isSuperAdmin ? openDebugForModule : undefined}
            />
          )}
        />
      )}

      {organizationId && resolvedReminderMember ? (
        <SendCertificationReminderModal
          open={reminderModalOpen}
          setOpen={(open) => {
            setReminderModalOpen(open);
            if (!open) setReminderInitialOverrides({});
          }}
          orgId={organizationId}
          token={token}
          members={[resolvedReminderMember]}
          initialOverrides={reminderInitialOverrides}
        />
      ) : null}

      {isSuperAdmin ? (
        <JsonInspectorModal
          open={Boolean(debugModuleId)}
          setOpen={(open) => {
            if (!open) setDebugModuleId(null);
          }}
          title={
            debugModuleId
              ? `Certification data — ${
                  debugDataRef.current.get(debugModuleId)?.catalog.title ??
                  debugModuleId
                }`
              : ""
          }
          value={debugModuleId ? debugDataRef.current.get(debugModuleId) : null}
        />
      ) : null}
    </div>
  );
}
